import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  rangeDep,
  type SkippedDependency,
} from './types';

/**
 * Parser for NuGet manifest files.
 *
 * Auto-detects the format from the content:
 *   - csproj / .props style: contains `<PackageReference`
 *   - packages.config style: contains `<package id=`
 *
 * Throws `ManifestParseError` when neither pattern is found.
 *
 * Version policy:
 *   - Literal version (1.2.3, 1.2.3.4, 1.2.3-beta1, etc.) → exact.
 *   - Wildcard (1.2.*) and range ([1.0,2.0), (,1.0]) → emitted as a range for
 *     the Worker to resolve to its upper bound.
 *   - MSBuild variable ($(Property)) → skipped ('MSBuild variable').
 *   - Missing Version entirely → skipped ('centrally managed version').
 */

// ─── Version classification ───────────────────────────────────────────────────

/** 4-part version with optional prerelease suffix: 1.2.3, 1.2.3.4, 1.2.3-beta1, etc. */
const EXACT_VERSION_RE = /^\d+(\.\d+){0,3}(-[A-Za-z0-9._-]+)?$/;

type VersionResult =
  | { kind: 'exact'; version: string }
  | { kind: 'range'; requirement: string }
  | { kind: 'skip'; reason: string };

function classifyNugetVersion(raw: string | null): VersionResult {
  if (raw === null || raw.trim() === '') {
    return { kind: 'skip', reason: 'centrally managed version' };
  }

  const v = raw.trim();

  // MSBuild variable: $(Property)
  if (v.startsWith('$(')) {
    return { kind: 'skip', reason: 'MSBuild variable' };
  }

  // Version range: starts with [ or (
  if (/^[[(]/.test(v)) {
    return { kind: 'range', requirement: v };
  }

  // Floating version: 1.2.*
  if (v.includes('*')) {
    return { kind: 'range', requirement: v };
  }

  if (EXACT_VERSION_RE.test(v)) {
    return { kind: 'exact', version: v };
  }

  // Anything else — treat as unknown/range.
  return { kind: 'range', requirement: v };
}

// ─── XML helpers ─────────────────────────────────────────────────────────────

/** Normalise CRLF → LF. */
function normalise(xml: string): string {
  return xml.replace(/\r\n/g, '\n');
}

/**
 * Extract the value of `attrName` from an XML element string.
 * Handles both single-quoted and double-quoted attribute values, case-insensitive
 * attribute name matching.
 */
function getAttribute(element: string, attrName: string): string | null {
  const re = new RegExp(`${attrName}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i');
  const m = element.match(re);
  if (!m) return null;
  return (m[1] ?? m[2] ?? '').trim();
}

/**
 * Extract the text content of a child element, e.g. `<Version>1.2.3</Version>`.
 */
function getChildText(element: string, tag: string): string | null {
  const re = new RegExp(`<${tag}[^>]*>([^<]*)<\\/${tag}>`, 'i');
  const m = element.match(re);
  return m ? (m[1] ?? '').trim() : null;
}

// ─── csproj / .props parser ───────────────────────────────────────────────────

/**
 * Parse `<PackageReference Include="..." Version="..." />` and the multi-line
 * element form:
 *
 * ```xml
 * <PackageReference Include="Foo">
 *   <Version>1.2.3</Version>
 * </PackageReference>
 * ```
 */
function parseCsproj(xml: string): ParsedManifest {
  const dependencies = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  // Match both self-closing and element forms.
  // Self-closing: <PackageReference ... />
  // Element form: <PackageReference ...> ... </PackageReference>
  const re = /<PackageReference\b([\s\S]*?)(?:\/\s*>|>([\s\S]*?)<\/PackageReference\s*>)/gi;
  let m: RegExpExecArray | null;

  while ((m = re.exec(xml)) !== null) {
    const attrs = m[1] ?? ''; // attribute string
    const innerContent = m[2] ?? ''; // element body (may be empty for self-closing)

    // Name comes from Include attribute.
    const name = getAttribute(attrs, 'Include');
    if (!name) continue;
    if (seen.has(name)) continue;
    seen.add(name);

    // Version: check attribute first, then child element.
    let versionRaw: string | null = getAttribute(attrs, 'Version');
    if (versionRaw === null && innerContent) {
      versionRaw = getChildText(innerContent, 'Version');
    }

    const result = classifyNugetVersion(versionRaw);
    if (result.kind === 'exact') {
      dependencies.push(dep('NUGET', name, result.version, versionRaw));
    } else if (result.kind === 'range') {
      dependencies.push(rangeDep('NUGET', name, result.requirement));
    } else {
      skipped.push({ name, requirement: versionRaw, reason: result.reason });
    }
  }

  return { system: 'NUGET', dependencies, skipped };
}

// ─── packages.config parser ──────────────────────────────────────────────────

/**
 * Parse `<package id="Name" version="1.2.3" ... />` entries.
 *
 * The `allowedVersions` attribute (a range constraint) is ignored — only the
 * `version` attribute is used.
 */
function parsePackagesConfig(xml: string): ParsedManifest {
  const dependencies = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>();

  const re = /<package\b([^>]*)\/?\s*>/gi;
  let m: RegExpExecArray | null;

  while ((m = re.exec(xml)) !== null) {
    const attrs = m[1] ?? '';

    const name = getAttribute(attrs, 'id');
    if (!name) continue;
    if (seen.has(name)) continue;
    seen.add(name);

    const versionRaw = getAttribute(attrs, 'version');

    const result = classifyNugetVersion(versionRaw);
    if (result.kind === 'exact') {
      dependencies.push(dep('NUGET', name, result.version, versionRaw));
    } else if (result.kind === 'range') {
      dependencies.push(rangeDep('NUGET', name, result.requirement));
    } else {
      skipped.push({ name, requirement: versionRaw, reason: result.reason });
    }
  }

  return { system: 'NUGET', dependencies, skipped };
}

// ─── Main parser ──────────────────────────────────────────────────────────────

/**
 * Parses a NuGet manifest (csproj/props or packages.config).
 *
 * Throws `ManifestParseError` when the input does not match either format.
 */
export const parseNuget: ManifestParser = (content: string): ParsedManifest => {
  const xml = normalise(content);

  const isCsproj = /<PackageReference\b/i.test(xml);
  const isPackagesConfig = /<package\s[^>]*\bid\s*=/i.test(xml);

  if (!isCsproj && !isPackagesConfig) {
    throw new ManifestParseError(
      'Input does not appear to be a NuGet manifest (no <PackageReference or <package id= found)',
    );
  }

  // csproj/props takes precedence when both patterns appear (unusual but safe).
  if (isCsproj) {
    return parseCsproj(xml);
  }

  return parsePackagesConfig(xml);
};
